import type { PermissionRule } from '@/shared/services/api/types';

/**
 * Évaluateur des permissions, coté client.
 *
 * Le serveur envoie ses regles telles quelles sur `GET /auth/me/permissions` ;
 * cet objet les évalue avec **le meme algorithme**. L'interface pose donc
 * exactement les memes questions que l'API :
 *
 *   if (can('listing', 'create', { shopId })) { … }
 *
 * et non `if (role === 'SELLER')`. Deux conséquences : un rôle ajouté ou une
 * regle modifiée coté backend se propagent sans toucher au mobile, et un bouton
 * affiché correspond toujours a une action réellement autorisée ; plus de 403
 * découvert au moment du clic.
 *
 * Ce contrôle reste un confort d'affichage : la décision qui compte est celle
 * du serveur, qui la reprend a chaque requete.
 */
export type Subject = Record<string, unknown>;

type Matcher =
  | string
  | number
  | boolean
  | null
  | { in: Array<string | number | boolean | null> }
  | { not: string | number | boolean | null };

export class PermissionSet {
  private readonly rules: Array<PermissionRule>;

  constructor(rules: Array<PermissionRule> = []) {
    this.rules = rules;
  }

  /**
   * `can('order', 'create')` : l'action est-elle envisageable ?
   * `can('product', 'update', product)` : sur cet objet précis ?
   * `can('product', 'update', product, 'price')` : sur ce champ ?
   */
  can(resource: string, action: string, subject?: Subject, field?: string): boolean {
    const candidates = this.rules.filter(
      (rule) =>
        rule.resource === resource &&
        rule.action === action &&
        (field === undefined || allowsField(rule, field)),
    );

    if (subject === undefined) {
      return candidates.length > 0;
    }
    return candidates.some((rule) => matches(subject, rule.conditions));
  }

  /** Champs modifiables pour cette action. `null` = aucun plafond. */
  allowedFields(resource: string, action: string, subject?: Subject): Array<string> | null {
    const applicable = this.rules.filter(
      (rule) =>
        rule.resource === resource &&
        rule.action === action &&
        (subject === undefined || matches(subject, rule.conditions)),
    );
    if (applicable.some((rule) => rule.fields === undefined || rule.fields === null)) {
      return null;
    }
    const fields = new Set<string>();
    for (const rule of applicable) {
      for (const field of rule.fields ?? []) {
        fields.add(field);
      }
    }
    return [...fields];
  }

  /** Le champ est-il modifiable ? Sert à griser une entrée de formulaire. */
  canEditField(resource: string, action: string, field: string, subject?: Subject): boolean {
    const allowed = this.allowedFields(resource, action, subject);
    return allowed === null || allowed.includes(field);
  }
}

/** Jeu vide : ce que voit un visiteur non connecté. */
export const NO_PERMISSIONS = new PermissionSet([]);

function allowsField(rule: PermissionRule, field: string): boolean {
  return rule.fields === undefined || rule.fields === null || rule.fields.includes(field);
}

function matches(subject: Subject, conditions: PermissionRule['conditions']): boolean {
  if (!conditions) {
    return true;
  }
  return Object.entries(conditions).every(([path, matcher]) =>
    matchesMatcher(resolvePath(subject, path), matcher as Matcher),
  );
}

function matchesMatcher(value: unknown, matcher: Matcher): boolean {
  if (matcher !== null && typeof matcher === 'object') {
    if ('in' in matcher) {
      return matcher.in.includes(value as never);
    }
    return value !== matcher.not;
  }
  return value === matcher;
}

/** `resolvePath(order, 'shop.ownerId')` → `order.shop?.ownerId`. */
function resolvePath(subject: Subject, path: string): unknown {
  return path.split('.').reduce<unknown>((current, key) => {
    if (current === null || current === undefined || typeof current !== 'object') {
      return undefined;
    }
    return (current as Record<string, unknown>)[key];
  }, subject);
}
